import React from 'react';
import PropTypes from 'prop-types';
import {usePreviewAutoSync} from '../hooks/usePreviewAutoSync';
import './PreviewAutoSyncPanel.css';

/**
 * PreviewAutoSyncPanel Component
 * Shows auto-sync status and allows manual sync trigger
 */
const PreviewAutoSyncPanel = ({projectId, serviceName, sessionId, enabled, inline, containerPort = 3001}) => {
  const {sendChanges, lastSentAt, pendingChanges} = usePreviewAutoSync(
    projectId,
    serviceName,
    sessionId,
    enabled,
    containerPort
  );

  const [isSending, setIsSending] = React.useState(false);
  const [lastResult, setLastResult] = React.useState(null);

  const handleManualSync = async () => {
    setIsSending(true);
    setLastResult(null);
    
    try {
      const result = await sendChanges();
      setLastResult(result);
    } catch (error) {
      setLastResult({
        success: false,
        message: error.message
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!enabled) {
    return null;
  }

  if (inline) {
    return (
      <div className="preview-auto-sync-inline">
        <div className="sync-status-inline">
          <span className="sync-icon">
            {pendingChanges > 0 ? '🔄' : '✓'}
          </span>
          <span className="sync-text">
            {pendingChanges > 0 ? `${pendingChanges} pending` : 'Synced'}
          </span>
        </div>
        
        <button
          className="sync-button-inline"
          disabled={isSending || pendingChanges === 0}
          onClick={handleManualSync}
          title="Manually sync changed files"
          type="button"
        >
          {isSending ? '⏳ Syncing...' : '↻ Sync Now'}
        </button>
      </div>
    );
  }

  return (
    <div className="preview-auto-sync-panel">
      <div className="sync-service-info">
        <span className="service-icon">{'🎨'}</span>
        <span className="service-name">{serviceName}</span>
      </div>
      
      <div className="sync-status">
        <span className="sync-icon">
          {pendingChanges > 0 ? '🔄' : '✓'}
        </span>
        <span className="sync-text">
          {pendingChanges > 0 ? `${pendingChanges} file(s) pending...`: 'All changes synced'}
        </span>
        {lastSentAt && (
          <span className="sync-time">
            {'Last synced: '}{new Date(lastSentAt).toLocaleTimeString()}
          </span>
        )}
      </div>
      
      <button
        className="sync-button"
        disabled={isSending || pendingChanges === 0}
        onClick={handleManualSync}
        title="Manually sync changed files"
        type="button"
      >
        {isSending ? '⏳ Syncing...' : '↻ Sync Now'}
      </button>

      {lastResult && (
        <div className={`sync-result ${lastResult.success ? 'success' : 'error'}`}>
          {lastResult.success ? '✓' : '✗'} {lastResult.message}
        </div>
      )}
    </div>
  );
};

PreviewAutoSyncPanel.propTypes = {
  containerPort: PropTypes.number,
  enabled: PropTypes.bool,
  inline: PropTypes.bool,
  projectId: PropTypes.string,
  serviceName: PropTypes.string,
  sessionId: PropTypes.string
};

PreviewAutoSyncPanel.defaultProps = {
  enabled: false,
  inline: false,
  projectId: null,
  serviceName: null,
  sessionId: null,
  containerPort: 3001
};

export default PreviewAutoSyncPanel;
